import {gql} from '@apollo/client';
import {useEffect} from 'react';

import {Antibody} from 'antibodies/AntibodyTypedefs';
import {AntibodySiteMarkerProps} from 'antibodies/visualization/AntibodySiteMarker';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {withoutSupersededAaSequenceNodes} from 'graphql-client/WithoutSupersededAaSequenceNode';
import {
  AdcDomainResiduesQuery,
  AdcDomainResiduesQueryVariables,
} from 'molbio/adcs/conjugation-site-markers/__generated/AdcDomainResiduesQuery';
import {
  DomainSequenceInfoByLabel,
  sitesToMarkers,
} from 'molbio/adcs/conjugation-site-markers/conjugationSiteMarkers';
import {ConjugationSite} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteConstants';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';

// The residues and antibody numbering of AA sequences.
export const ADC_DOMAIN_RESIDUES_QUERY = gql`
  query AdcDomainResiduesQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: MOLBIO) {
    aaSequences(ids: $ids) {
      ... on AaSequenceNode {
        id
        experimentalCurrentVersion {
          dbId
          aminoAcids
          antibodyNumberingInfo {
            numbering
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const CONJUGATION_SITES_UNAVAILABLE_TOAST_ID = 'adc-conjugation-sites-unavailable';
export const CONJUGATION_SITES_UNAVAILABLE_MESSAGE = 'Some conjugation sites could not be shown.';

interface DomainEntities {
  // The id of the AA sequence entity of each domain whose entity the user can view, by the domain's format label.
  entityIdsByLabel: Record<string, string>;
  // The format labels of the domains whose entity the user can't view.
  inaccessibleLabels: Set<string>;
}

function domainEntitiesOf(antibody: Antibody): DomainEntities {
  const entityIdsByLabel: Record<string, string> = {};
  const inaccessibleLabels = new Set<string>();
  for (const formatDomain of antibody.format.domains) {
    // A domain of the format is the domain of the version that is drawn at the same positions (refs). An
    // optional domain with no sequence has no domain in the version.
    const versionDomain = antibody.version?.domains.find((domain) =>
      domain.refs.some((ref) => formatDomain.refs.includes(ref))
    );
    if (versionDomain == null) {
      continue;
    }
    if (isInaccessible(versionDomain.entity)) {
      inaccessibleLabels.add(formatDomain.label);
    } else {
      entityIdsByLabel[formatDomain.label] = versionDomain.entity.id;
    }
  }
  return {entityIdsByLabel, inaccessibleLabels};
}

/**
 * The markers for conjugation sites on the antibody, to draw on top of it. Loads the residues and antibody
 * numbering of the domains that the sites are on, and gives no markers until they have loaded.
 *
 * A site on a domain whose sequence the user can't view is left out, since its numbering is not available.
 * The user is warned of that, and if the residues can't be loaded, in which case there are no markers.
 *
 * @param antibody The antibody, with the version to draw the sites on, or null if it hasn't loaded.
 * @param sites The sites to mark, or undefined to mark none.
 */
export function useConjugationSiteMarkers(
  antibody: Antibody | null,
  sites: ReadonlyArray<ConjugationSite> | undefined
): Array<AntibodySiteMarkerProps> {
  const {entityIdsByLabel, inaccessibleLabels} =
    antibody == null
      ? {entityIdsByLabel: {}, inaccessibleLabels: new Set<string>()}
      : domainEntitiesOf(antibody);
  const labelsWithSites = new Set((sites ?? []).map((site) => site.domainLabel));
  const entityIdsToFetch = Array.from(
    new Set(
      Object.entries(entityIdsByLabel)
        .filter(([label]) => labelsWithSites.has(label))
        .map(([, id]) => id)
    )
  );

  const result = useSkippableBenchlingQuery<AdcDomainResiduesQuery, AdcDomainResiduesQueryVariables>(
    ADC_DOMAIN_RESIDUES_QUERY,
    {variables: {ids: entityIdsToFetch}, skip: antibody == null || entityIdsToFetch.length === 0}
  );

  // With no entities to load, there is nothing to wait for.
  const isLoaded = result.type === QueryResultType.SUCCESS || entityIdsToFetch.length === 0;
  let markers: Array<AntibodySiteMarkerProps> = [];
  let hasUnviewableSites = false;
  if (antibody != null && sites != null && isLoaded) {
    const residuesByEntityId = new Map<string, DomainSequenceInfoByLabel[string]>();
    if (result.type === QueryResultType.SUCCESS) {
      for (const sequence of withoutSupersededAaSequenceNodes(result.data.aaSequences)) {
        if (isInaccessible(sequence)) {
          continue;
        }
        const {aminoAcids, antibodyNumberingInfo} = sequence.experimentalCurrentVersion;
        residuesByEntityId.set(sequence.id, {
          sequence: aminoAcids,
          numbering: antibodyNumberingInfo == null ? null : antibodyNumberingInfo.numbering,
        });
      }
    }
    const domainResidues: Record<string, DomainSequenceInfoByLabel[string]> = {};
    const unviewableLabels = new Set(inaccessibleLabels);
    for (const [label, entityId] of Object.entries(entityIdsByLabel)) {
      const residues = residuesByEntityId.get(entityId);
      if (residues == null) {
        // The entity was viewable in the antibody but not when loading its residues.
        unviewableLabels.add(label);
      } else {
        domainResidues[label] = residues;
      }
    }
    const viewableSites = sites.filter((site) => !unviewableLabels.has(site.domainLabel));
    hasUnviewableSites = viewableSites.length < sites.length;
    markers = sitesToMarkers(antibody.format, domainResidues, viewableSites);
  }

  const shouldWarn = hasUnviewableSites || result.type === QueryResultType.ERROR;
  useEffect(() => {
    if (shouldWarn) {
      // A fixed id so that the toast isn't repeated if more than one map or render reports the problem.
      ToastActions.pushToast({
        id: CONJUGATION_SITES_UNAVAILABLE_TOAST_ID,
        message: CONJUGATION_SITES_UNAVAILABLE_MESSAGE,
        type: ToastType.WARNING,
      });
    }
  }, [shouldWarn]);

  return markers;
}
